import { clientFetch } from '~/lib/.client/fetch';
import { requestWithAuth } from '~/lib/.client/request-with-auth';
import { CLIENT_ORIGIN, IGNORE_BLOB_ID } from '~/lib/constants';
import { updateChat } from '~/lib/persistence/chats';
import type { AppFileEntry, AppFiles, ForkedProjectData, ProjectVisibility, SoftForkUpdate } from '~/lib/projects';
import type { Dirent, FileMap } from '~/lib/stores/files';
import { userStore } from '~/lib/stores/user';
import type { OrganizationData } from '~/types/organization';
import { WORK_DIR } from '~/utils/constants';
import { createGitIgnoreCheckForFiles } from '~/utils/ignore';
import { invariant } from '~/utils/invariant';
import { logger } from '~/utils/logger';
import { addWorkDir } from '~/utils/workDir';

const DOTENV_PATH = addWorkDir('.env');

const requestProjectWithAuth = requestWithAuth(CLIENT_ORIGIN);

export async function createEmptyProject(
  activeOrganization: OrganizationData | null,
  extraParams?: {
    project?: {
      title?: string;
      appFiles?: AppFiles;
    };
  },
): Promise<ForkedProjectData> {
  logger.debug('Create empty project');

  const params = activeOrganization
    ? {
        ...extraParams,
        owner: activeOrganization.slug,
        provider: activeOrganization.provider,
      }
    : { ...extraParams };

  const result = await requestProjectWithAuth<SoftForkUpdate>(
    `api/projects/sb1/fork`,
    { project: { ...params.project, appFiles: {} }, ...params },
    'POST',
  );

  // you can only create an empty project if you are already logged in
  invariant(result);

  return {
    ...result,
    id: String(result.id),
  };
}

export async function forkProject(options: {
  slug: string;
  files: FileMap;
  activeOrganization: OrganizationData | null;
}): Promise<ForkedProjectData> {
  const { slug, files, activeOrganization } = options;

  const appFiles = mapFilesRespectingTopLevelGitIgnoreToAppFiles(files);

  const params = activeOrganization
    ? {
        owner: activeOrganization.slug,
        provider: activeOrganization.provider,
      }
    : {};

  const response = await requestProjectWithAuth<SoftForkUpdate>(
    `api/projects/${slug}/fork`,
    { project: { appFiles }, ...params },
    'POST',
  );

  // you can only fork a project when you are already logged in
  invariant(response);

  const idAfterFork = String(response.id);
  const slugAfterFork = response.slug;
  const editedAtAfterFork = response.editedAt;

  return {
    id: idAfterFork,
    slug: slugAfterFork,
    editedAt: editedAtAfterFork,
    access: response.access,
    visibility: response.visibility,
    frozen: response.frozen,
    organization: response.organization,
    integrations: response.integrations,
  };
}

export async function patchProject(slug: string, properties: Record<'title', string>) {
  const { title } = properties;

  await requestProjectWithAuth<{ editedAt: string }>(`api/projects/${slug}`, { project: { title } }, 'PATCH');
}

export async function patchProjectVisibility(slug: string, visibility: ProjectVisibility) {
  await requestProjectWithAuth<{ editedAt: string }>(`api/projects/${slug}`, { project: { visibility } }, 'PATCH');
}

export async function patchHostingProvider(slug: string, provider: 'bolt' | 'netlify') {
  await requestProjectWithAuth(
    `/api/projects/${slug}/integrations/hosting`,
    { projectIntegration: { data: { hostingProvider: provider } } },
    'PUT',
  );
}

export async function saveProject(options: {
  slug: string;
  editedAt: string;
  files: FileMap;
  conflictHandler?: (lastEditor: string, editedAt: string) => void;
  fork?: () => void;
  projectTooBigToSave?: () => void;
  force?: boolean;
}): Promise<{ id: string; editedAt: string; title: string } | undefined> {
  const { slug, editedAt, files, force = false, conflictHandler } = options;

  /**
   * Make sure user is still logged in before updating project on Stackblitz side.
   * The project API on Stackblitz does not require authorization like Bolt's side does.
   * This is to avoid conflicting editedAt timestamps.
   */
  await assertSession();

  const appFiles = mapFilesRespectingTopLevelGitIgnoreToAppFiles(files);

  const response = await requestProjectWithAuth<{ id: string; editedAt: string; title: string }>(
    `api/projects/${slug}`,
    { project: { appFiles, editedAt: force ? undefined : editedAt } },
    'PATCH',
    {
      409: async (response: Response) => {
        const { lastEditor, editedAt } = (await response.json()) satisfies {
          lastEditor: { username: string };
          editedAt: string;
        };

        conflictHandler?.(lastEditor.username, editedAt);

        return true;
      },
      422: async (response) => {
        const body = await response.json();

        if (isSaveProjectError(body) && body.errors.app_files) {
          if (body.errors.app_files.includes('files changed for frozen project')) {
            options.fork?.();

            return true;
          }
        }

        return false;
      },
      413: async () => {
        options.projectTooBigToSave?.();

        return true;
      },
    },
  );

  if (!response) {
    return undefined;
  }

  updateChat(response.id, { editedAt: response.editedAt, description: response.title });

  return {
    id: response.id,
    editedAt: response.editedAt,
    title: response.title,
  };
}

async function assertSession() {
  const response = await clientFetch('/api/logged-in');
  const json = await response.json<{ isLoggedIn?: boolean }>();

  if (json?.isLoggedIn === true) {
    return;
  }

  userStore.setLogout();

  throw new Error('Not logged in');
}

function mapDirentToAppFile(filePath: string, dirent: Dirent): AppFileEntry {
  if (dirent.type === 'file') {
    return {
      fullPath: filePath,
      contents: dirent?.type === 'file' ? dirent.content : '',
      name: basename(filePath),
      isBinary: dirent?.type === 'file' ? dirent.isBinary ?? false : false,
      type: 'file',
      lastModified: Date.now(),
      blobId: dirent.blobId,
    };
  } else {
    return {
      type: 'folder',
      fullPath: filePath,
      name: basename(filePath),
      lastModified: Date.now(),
    };
  }
}

function mapFilesRespectingTopLevelGitIgnoreToAppFiles(files: FileMap) {
  const appFiles: AppFiles = {};

  const isFileIgnored = createGitIgnoreCheckForFiles(files);

  if (Object.keys(files).length === 0) {
    return Promise.resolve();
  }

  for (const [filePath, file] of Object.entries(files)) {
    if (!file) {
      continue;
    }

    if (
      file.type === 'file' &&
      filePath !== DOTENV_PATH &&
      (isFileIgnored(filePath) || file.blobId === IGNORE_BLOB_ID)
    ) {
      continue;
    }

    const relativePath = filePath.substring(WORK_DIR.length + 1);

    appFiles[relativePath] = mapDirentToAppFile(relativePath, file);
  }

  return appFiles;
}

interface SaveProjectErrorResponse {
  errors: {
    app_files?: string[];
  };
}

function isSaveProjectError(body: unknown): body is SaveProjectErrorResponse {
  return typeof body === 'object' && body !== null && 'errors' in body;
}

function basename(path: string): string {
  return path.split('/').pop()!;
}
